// Maps Encompass TPO loan data to Reggora fields using the lender's TPO field mappings.
//
// Two payload shapes are supported:
// - mapLegacyLoanData: tpoApplication.getLoanData() (deprecated) — PascalCase keys, /Date(ms)/ dates
// - parseLoanAllData: loan.all() — camelCase keys, ISO dates, custom fields as { fieldName, value, format }
// Both accept the same lender mapping keys (e.g. Applications__Borrower__FirstName).

export function mapLegacyLoanData(tpoData, lenderMapping) {
  const mapped_values = {}

  const dateTimeRetriever = function(key, value) {
    let a
    if (typeof value === 'string') {
      a = /\/Date\((-?\d*)(-\d{4})?\)\//.exec(value)

      if (a) {
        return new Date(+a[1])
      }
    }
    return value
  }
  // dates sometimes come in w/ the format /Date(123445566677)/ We need to parse them into a format we can understand
  tpoData = JSON.parse(JSON.stringify(tpoData), dateTimeRetriever)


  for (const [key, value] of Object.entries(lenderMapping)) {
    const split_key = key.split('__')
    let curr_elem = tpoData
    let field_found = true

    for (const curr_key of split_key) {
      if (!(curr_key in curr_elem)) {
        field_found = false
        break
      }

      // Applications data
      if (curr_key === 'Applications') {
        curr_elem = curr_elem[curr_key][0]
      } else if (curr_key === 'Contacts') {
        let contactType = split_key.pop()
        let contactName = 'Name'

        // We need to support both Name and ContactName fields from the loan data
        // Getting the ContactName field from the field mapping. Ex: Contacts__SELLERS_AGENT_ContactName
        const contactParts = contactType.split('_')
        if (contactParts.length === 3) {
          contactType = contactParts[0] + '_' + contactParts[1]
          contactName = contactParts[2]
        }

        const contact = tpoData.Contacts.find(elem => elem.ContactType === contactType)

        if (contact && contactName in contact) {
          mapped_values[value + ' Name'] = contact[contactName]
          mapped_values[value + ' Email'] = 'Email' in contact ? contact.Email : ''
          mapped_values[value + ' Phone'] = 'Phone' in contact ? contact.Phone : ''
        }
        field_found = false
        break
      } else {
        curr_elem = curr_elem[curr_key]

        // Fees and Residences are array of objects
        // Need to retrieve appropriate object for curr_elem based on condition
        if (Array.isArray(curr_elem)) {
          if (curr_key === 'Fees') {
            const feeObj = curr_elem.find(e => e.FeeType === 'AppraisalFee')
            if (feeObj) {
              curr_elem = feeObj
            }
          } else if (curr_key === 'Residences') {
            const residenceObj = curr_elem.find(e => e.ApplicantType === 'Borrower' && e.ResidencyType === 'Current')
            if (residenceObj) {
              curr_elem = residenceObj
            }
          } else if (curr_key === 'CustomFields') {
            // Custom field mapping format: CustomFields__{MortgageType}__{FieldId}
            // Ex:CustomFields__FHA__CX_REGGORA_FHACASEASSIGNMENT
            const mappedFields = key.split('__')
            if (mappedFields && mappedFields.length === 3) {
              if ('MortgageType' in tpoData && tpoData.MortgageType === mappedFields[1]) {
                const mappedField = mappedFields[2].replaceAll('_', '.')
                const customFieldObj = curr_elem.find(f => f.FieldName === mappedField)
                if (customFieldObj && 'StringValue' in customFieldObj) {
                  curr_elem = customFieldObj.StringValue
                  break
                }
              }
            }
          }
        }
      }
    }

    if (field_found) {
      // Allow import of one TPO field to multiple Reggore fields
      // Reggora fields should be comma separated
      if (value.indexOf(',') > 0) {
        const reggora_fields = value.split(',')
        for (const reggora_field of reggora_fields) {
          const trimed_reggora_field = reggora_field.trim()
          if (trimed_reggora_field) {
            mapped_values[trimed_reggora_field] = curr_elem
          }
        }
      } else {
        mapped_values[value] = curr_elem
      }
    }
  }

  return mapped_values
}

// Key under which loan.all() mapped data is sent alongside the legacy mapped_data for comparison logging
export const LOAN_ALL_MAPPED_DATA_KEY = '_loan_all_mapped_data'

// loan.all() keys are the legacy key names with the first letter lowercased (LoanIdNumber -> loanIdNumber),
// except for the renamed and acronym fields below, keyed by the exact legacy name used in lender mappings.
const LOAN_ALL_FIELD_ALIASES = {
  EncompassId: 'id',
  TPO: 'tpo',
  ULDD: 'uldd',
  TQL: 'tql',
  CompanyID: 'companyId',
  CompanyAEUserName: 'companyAeUserName',
  LOName: 'loName',
  LOEmail: 'loEmail',
  LOID: 'loId',
  LOBusinessPhone: 'loBusinessPhone',
  LOCellPhone: 'loCellPhone',
  LOAEName: 'loAeName',
  LOAEUserName: 'loAeUserName',
  LPName: 'lpName',
  LPEmail: 'lpEmail',
  LPID: 'lpId',
  LPAEName: 'lpAeName',
  LPAEUserName: 'lpAeUserName',
}

export function toLoanAllKey(legacyKey) {
  return LOAN_ALL_FIELD_ALIASES[legacyKey] || legacyKey.charAt(0).toLowerCase() + legacyKey.slice(1)
}

const isPlainObject = obj => obj !== null && typeof obj === 'object' && !Array.isArray(obj)

function getLoanAllField(obj, legacyKey) {
  const key = toLoanAllKey(legacyKey)
  return isPlainObject(obj) && Object.prototype.hasOwnProperty.call(obj, key)
    ? { found: true, value: obj[key] }
    : { found: false, value: undefined }
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/

// Legacy dates were /Date(ms)/ converted to Date objects; convert ISO strings the same way so the
// serialized mapped_data is unchanged. Date-only strings parse as UTC midnight, matching legacy values.
export function toDateIfIso(value) {
  if (typeof value === 'string' && ISO_DATE_REGEX.test(value)) {
    const date = new Date(value)
    if (!isNaN(date.getTime())) {
      return date
    }
  }
  return value
}

// Legacy custom fields exposed StringValue; loan.all() returns a typed value instead
function customFieldStringValue(customField) {
  const { value, format } = customField
  if (value === undefined || value === null) {
    return undefined
  }
  if (format === 'DATE' && typeof value === 'string') {
    const dateParts = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
    if (dateParts) {
      return `${dateParts[2]}/${dateParts[3]}/${dateParts[1]}`
    }
  }
  return String(value)
}

function resolveCustomField(loanData, customFields, splitKey) {
  // Custom field mapping format: CustomFields__{MortgageType}__{FieldId}
  // Ex: CustomFields__FHA__CX_REGGORA_FHACASEASSIGNMENT
  if (splitKey.length !== 3 || loanData.mortgageType !== splitKey[1]) {
    return { found: false }
  }
  const fieldName = splitKey[2].replaceAll('_', '.')
  const customField = customFields.find(f => isPlainObject(f) && f.fieldName === fieldName)
  const value = customField && customFieldStringValue(customField)
  return value === undefined ? { found: false } : { found: true, value }
}

function mapContact(loanData, contactKey, reggoraField, mappedValues) {
  let contactType = contactKey
  let contactName = 'Name'

  // We need to support both Name and ContactName fields from the loan data
  // Getting the ContactName field from the field mapping. Ex: Contacts__SELLERS_AGENT_ContactName
  const contactParts = contactType.split('_')
  if (contactParts.length === 3) {
    contactType = contactParts[0] + '_' + contactParts[1]
    contactName = contactParts[2]
  }

  if (!Array.isArray(loanData.contacts)) {
    return
  }
  const contact = loanData.contacts.find(c => isPlainObject(c) && c.contactType === contactType)
  const name = getLoanAllField(contact, contactName)

  if (name.found) {
    mappedValues[reggoraField + ' Name'] = name.value
    mappedValues[reggoraField + ' Email'] = 'email' in contact ? contact.email : ''
    mappedValues[reggoraField + ' Phone'] = 'phone' in contact ? contact.phone : ''
  }
}

// Allow import of one TPO field to multiple Reggora fields
// Reggora fields should be comma separated
function assignMappedValue(mappedValues, reggoraField, value) {
  if (reggoraField.indexOf(',') > 0) {
    for (const field of reggoraField.split(',')) {
      const trimmedField = field.trim()
      if (trimmedField) {
        mappedValues[trimmedField] = value
      }
    }
  } else {
    mappedValues[reggoraField] = value
  }
}

function resolvePath(loanData, splitKey) {
  let currElem = loanData

  for (const currKey of splitKey) {
    const field = getLoanAllField(currElem, currKey)
    if (!field.found) {
      return { found: false }
    }
    currElem = field.value

    // Applications data
    if (currKey === 'Applications') {
      currElem = Array.isArray(currElem) ? currElem[0] : currElem
    } else if (Array.isArray(currElem)) {
      // Fees and Residences are array of objects
      // Need to retrieve appropriate object for currElem based on condition
      if (currKey === 'Fees') {
        currElem = currElem.find(e => isPlainObject(e) && e.feeType === 'AppraisalFee') || currElem
      } else if (currKey === 'Residences') {
        currElem = currElem.find(e => isPlainObject(e) && e.applicantType === 'Borrower' &&
          e.residencyType === 'Current') || currElem
      } else if (currKey === 'CustomFields') {
        return resolveCustomField(loanData, currElem, splitKey)
      }
    }
  }

  return { found: true, value: toDateIfIso(currElem) }
}

export function parseLoanAllData(loanData, lenderMapping) {
  const mappedValues = {}

  for (const [rawKey, reggoraField] of Object.entries(lenderMapping)) {
    const splitKey = rawKey.trim().split('__')

    if (splitKey[0] === 'Contacts') {
      mapContact(loanData, splitKey[splitKey.length - 1], reggoraField, mappedValues)
      continue
    }

    const field = resolvePath(loanData, splitKey)
    if (field.found) {
      assignMappedValue(mappedValues, reggoraField, field.value)
    }
  }

  return mappedValues
}
